import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { getBuilderStatus, requestAgentBridgeUrl } from '../../services/api';
import type { ToolOpportunity } from '../../types/aai';

/*
 * What one tool is good for, read against that tool. The arc is one row:
 * found -> open it in the workspace, where it is built -> the workspace says
 * it is finished -> make it available. Only something finished can be made
 * available, and making it available is still its own decision.
 */

const KIND_WORD: Record<string, string> = {
  skill: 'Skill',
  plugin: 'Plugin',
  build: 'Build',
};

const STATUS_WORD: Record<string, string> = {
  open: 'Not decided',
  converted: 'Skill drafted',
  building: 'Being built',
  built: 'Ready',
  enabled: 'Available',
  dismissed: 'Declined',
};

/* The request is one call with no progress events, so these are the steps
   the server takes, advanced on a clock and held on the last one. A connected
   tool is read first; one that is not is reasoned from company context. */
const stepsFor = (toolLabel: string, connected: boolean): string[] => [
  connected
    ? `Reading ${toolLabel} with read-only access`
    : `Reading what your company has told Orbit about ${toolLabel}`,
  connected
    ? 'Checking what it finds against your company context'
    : `Looking for work people do by hand in ${toolLabel}`,
  'Deciding whether each one is a skill, a plugin or a build',
  'Writing up what each one would do',
];

const STEP_MS = 4500;

const FindProgress: React.FC<{ toolLabel: string; connected: boolean }> = ({ toolLabel, connected }) => {
  const steps = stepsFor(toolLabel, connected);
  const [at, setAt] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setAt((current) => Math.min(current + 1, steps.length - 1));
    }, STEP_MS);
    return () => window.clearInterval(timer);
  }, [steps.length]);

  return (
    <div className="topp-progress" role="status" aria-live="polite">
      <b>Orbit is looking through {toolLabel}</b>
      <ol>
        {steps.map((step, i) => (
          <li key={step} className={clsx(i < at && 'done', i === at && 'now')}>
            <span className="dot" aria-hidden="true" />
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
};

const detailOf = (err: unknown): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || '';

export const ToolOpportunities: React.FC<{
  toolKey: string;
  toolLabel: string;
  connected: boolean;
  clientId: string;
  /** Re-read the board, so the tool's own count follows a decision taken here. */
  onChanged?: () => void;
}> = ({ toolKey, toolLabel, connected, clientId, onChanged }) => {
  const [items, setItems] = useState<ToolOpportunity[] | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await aaiApi.getToolOpportunities({ tool_key: toolKey });
      setItems(res.data.opportunities);
      setCanManage(Boolean(res.data.can_manage));
    } catch {
      setItems([]);
    }
  }, [toolKey]);
  useEffect(() => { load(); }, [load]);

  const find = async () => {
    setBusy(true);
    try {
      const res = await aaiApi.generateToolOpportunities({ tool_key: toolKey, tool_label: toolLabel });
      setItems(res.data.opportunities);
      if (res.data.created === 0) {
        toast.info(`Nothing new in ${toolLabel} this time.`);
      } else {
        toast.success(`${res.data.created} found in ${toolLabel}.`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(detailOf(err) || `Could not look through ${toolLabel}.`);
    } finally {
      setBusy(false);
    }
  };

  const replace = (next: ToolOpportunity) => {
    setItems((current) => (current || []).map((i) => (i.id === next.id ? next : i)));
    onChanged?.();
  };

  const act = async (id: string, action: string, run: () => Promise<void>) => {
    setActing(`${action}:${id}`);
    try {
      await run();
    } finally {
      setActing('');
    }
  };

  const openWorkspace = (item: ToolOpportunity) => act(item.id, 'workspace', async () => {
    // The tab is opened inside the click, before any request: a window.open
    // that waits on round trips has lost the click by then, and the browser
    // blocks it silently, so the first click only marked the row. It says
    // what is happening rather than sitting on a blank page.
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
      tab.document.title = 'Opening the workspace';
      tab.document.body.style.cssText = 'font: 14px system-ui, sans-serif; color: #555; display: grid; place-items: center; height: 100vh; margin: 0;';
      tab.document.body.textContent = 'Opening the workspace\u2026';
    }
    const fail = (message: string) => {
      tab?.close();
      toast.error(message);
    };
    try {
      // Only the id crosses: the crossing keeps 256 characters, so the
      // workspace reads the full opening message back from aibot itself.
      // Nothing here needs another's answer, so the three go at once.
      const redirect = `/customer/workspace?workspaceId=${encodeURIComponent(clientId)}`
        + `&opportunityId=${encodeURIComponent(item.id)}`
        + `${connected ? '' : '&connected=0'}`;
      const [status, res, bridge] = await Promise.all([
        getBuilderStatus().catch(() => null),
        aaiApi.openToolOpportunityWorkspace(item.id),
        requestAgentBridgeUrl(redirect, clientId || undefined).catch(() => null),
      ]);
      if (!status?.validxAppUrl) {
        fail('The build workspace is not connected for this company yet.');
        return;
      }
      if (!bridge?.url) {
        fail('Could not open the build workspace.');
        return;
      }
      replace(res.data.opportunity);
      if (tab) {
        tab.location.href = bridge.url;
      } else {
        window.location.assign(bridge.url);
      }
      if (!connected) {
        toast.info(`${toolLabel} is not connected yet. Connect it before you make this available.`);
      }
    } catch (err) {
      fail(detailOf(err) || 'Could not open the build workspace.');
    }
  });

  const makeAvailable = (item: ToolOpportunity) => act(item.id, 'enable', async () => {
    try {
      const res = await aaiApi.enableToolOpportunity(item.id);
      replace(res.data.opportunity);
      toast.success(`Available to ${item.audience === 'Everyone' ? 'everyone' : item.audience} in the workspace and the marketplace.`);
    } catch (err) {
      toast.error(detailOf(err) || 'Could not make it available.');
    }
  });

  const dismiss = (item: ToolOpportunity) => act(item.id, 'dismiss', async () => {
    try {
      const res = await aaiApi.dismissToolOpportunity(item.id);
      replace(res.data.opportunity);
    } catch (err) {
      toast.error(detailOf(err) || 'Could not decline it.');
    }
  });

  const shown = (items || []).filter((i) => i.status !== 'dismissed');

  return (
    <div className="topp">
      <div className="topp-bar">
        <b>What this tool is good for</b>
        {canManage && (
          <button type="button" disabled={busy} onClick={find}>
            {busy ? 'Looking' : 'Find opportunities'}
          </button>
        )}
      </div>

      {busy && <FindProgress toolLabel={toolLabel} connected={connected} />}
      {items === null && !busy && (
        <p className="topp-empty topp-loading"><span className="dot" aria-hidden="true" />Loading what Orbit found here.</p>
      )}
      {items !== null && shown.length === 0 && !busy && (
        <p className="topp-empty">
          Nothing found in {toolLabel} yet.{' '}
          {canManage
            ? 'Ask Orbit to look: it reads the tool where it can, and what your company has already told it where it cannot.'
            : 'Orbit raises these as it learns how your company works.'}
        </p>
      )}

      {shown.length > 0 && (
        <div className="topp-list">
          {shown.map((item) => (
            <div key={item.id} className="topp-row">
              <span className="t">
                <strong>{item.title}</strong>
                <small>{item.summary}</small>
                {item.proposed?.kind_reason && <small className="why">{item.proposed.kind_reason}</small>}
              </span>
              <span className="m">
                <b className="kind">{KIND_WORD[item.kind] || 'Skill'}</b>
                <em>{item.audience}</em>
                <i className={clsx((item.status === 'enabled' || item.status === 'built') && 'ok')}>
                  {STATUS_WORD[item.status] || item.status}
                </i>
              </span>
              {canManage && (
                <span className="acts">
                  <button
                    type="button"
                    className="pri"
                    disabled={Boolean(acting)}
                    onClick={() => { void openWorkspace(item); }}
                  >
                    {acting === `workspace:${item.id}` && 'Opening'}
                    {acting !== `workspace:${item.id}` && (item.status === 'open' ? 'Open in workspace' : 'Continue in workspace')}
                  </button>
                  {item.status === 'built' && (
                    <button type="button" disabled={Boolean(acting)} onClick={() => { void makeAvailable(item); }}>
                      {acting === `enable:${item.id}` ? 'Making available' : 'Make available'}
                    </button>
                  )}
                  {item.status === 'open' && (
                    <button
                      type="button"
                      className="hide"
                      title="Hide it. It leaves this list and is not suggested again for this tool."
                      disabled={Boolean(acting)}
                      onClick={() => { void dismiss(item); }}
                    >
                      {acting === `dismiss:${item.id}` ? 'Hiding' : 'Hide'}
                    </button>
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolOpportunities;
